import {
    BankOutlined,
    DollarOutlined,
    MinusCircleOutlined,
    PlusCircleOutlined,
    SaveOutlined,
    SafetyCertificateOutlined,
    ShopOutlined,
    SwapOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Form,
    Input,
    InputNumber,
    message,
    Row,
    Select,
    Statistic,
    Table,
    Tabs,
    Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../contexts/ApiContext';
import { useCashDrawer } from '../contexts/CashDrawerContext';
import useCashBookingReadiness from '../hooks/useCashBookingReadiness';
import { CashBookingRefusalNotice } from '../components/CashBookingRefusal';
import {
    calculateDenominationTotal,
    DEFAULT_DENOMINATIONS,
    DENOMINATION_LABELS,
    type Denominations,
} from '../domain/dispozitii/denominations';
import type {
    DispozitiiRow,
    DispozitiiSummary,
    ExpenseEntry,
    ExpenseType,
    TransferTargets,
} from '../domain/dispozitii/domain';

const { Title, Text } = Typography;

const EXPENSE_TYPES: ExpenseType[] = [
    'phone',
    'consumption',
    'office_supplies',
    'salary',
    'loss',
    'tax',
    'advertising',
    'other',
];

interface AmountOperationState {
    amount: number | null;
    reprezentand: string;
    delegat: string;
    act: string;
    rc: boolean;
}

interface DenominationOperationState {
    denominations: Denominations;
    reprezentand: string;
    delegat: string;
    act: string;
    rc: boolean;
}

const emptyAmountState = (): AmountOperationState => ({
    amount: null,
    reprezentand: '',
    delegat: '',
    act: '',
    rc: true,
});

const emptyDenominationState = (): DenominationOperationState => ({
    denominations: DEFAULT_DENOMINATIONS,
    reprezentand: '',
    delegat: '',
    act: '',
    rc: true,
});

const Dispozitii = () => {
    const { t } = useTranslation();
    const { makeRequest } = useApi();
    const { cashPointId } = useCashDrawer();
    // Every form here moves money in or out of this drawer: a station that cannot book it
    // is told so on opening the page, and the forms cannot be saved, instead of each one
    // being refused after it was filled in.
    const { refusalKey: bookingRefusalKey } = useCashBookingReadiness({ checkOnMount: true });
    const bookingBlocked = bookingRefusalKey !== null;
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [summary, setSummary] = useState<DispozitiiSummary>({
        safeBalance: 0,
        bankBalance: 0,
        dispositions: [],
    });
    const [expenses, setExpenses] = useState<ExpenseEntry[]>([]);

    const [associateCollection, setAssociateCollection] =
        useState<AmountOperationState>(emptyAmountState());
    const [associatePayment, setAssociatePayment] =
        useState<AmountOperationState>(emptyAmountState());
    const [bankCollection, setBankCollection] = useState<AmountOperationState>(emptyAmountState());
    const [bankPayment, setBankPayment] = useState<AmountOperationState>(emptyAmountState());
    const [supplierPayment, setSupplierPayment] =
        useState<AmountOperationState>(emptyAmountState());
    const [safePickup, setSafePickup] =
        useState<DenominationOperationState>(emptyDenominationState());

    const [transferDenominations, setTransferDenominations] =
        useState<Denominations>(DEFAULT_DENOMINATIONS);
    const [transferToCashPointId, setTransferToCashPointId] = useState<number | null>(null);
    const [transferReceiverUserId, setTransferReceiverUserId] = useState<number | null>(null);
    const [transferObservations, setTransferObservations] = useState('');
    const [transferTargets, setTransferTargets] = useState<TransferTargets>({
        cashPoints: [],
        users: [],
    });
    const [transferTargetsLoading, setTransferTargetsLoading] = useState(false);
    const [expenseType, setExpenseType] = useState<ExpenseType>('phone');
    const [expenseDescription, setExpenseDescription] = useState('');
    const [expenseAmount, setExpenseAmount] = useState<number | null>(null);
    const [expenseNotes, setExpenseNotes] = useState('');
    const [expenseSaving, setExpenseSaving] = useState(false);
    // An expense is paid from the till's open session, so it can only be recorded for today
    // (cash session audit item 7); a past day's list is read-only.
    const expenseDayIsToday = selectedDate.isSame(dayjs(), 'day');

    const safePickupTotal = useMemo(
        () => calculateDenominationTotal(safePickup.denominations),
        [safePickup.denominations],
    );
    const transferTotal = useMemo(
        () => calculateDenominationTotal(transferDenominations),
        [transferDenominations],
    );

    const fetchSummary = useCallback(async () => {
        setLoading(true);
        try {
            const dateStr = selectedDate.format('YYYY-MM-DD');
            const res = await makeRequest<DispozitiiSummary>(`/dispozitii/summary?date=${dateStr}`);
            if (res.success && res.data) {
                setSummary(res.data);
            }
        } catch {
            message.error(t('dispozitii.fetchError'));
        } finally {
            setLoading(false);
        }
    }, [selectedDate, makeRequest, t]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    const fetchExpenses = useCallback(async () => {
        try {
            const dateStr = selectedDate.format('YYYY-MM-DD');
            const res = await makeRequest<ExpenseEntry[]>(`/dispozitii/expenses?date=${dateStr}`);
            if (res.success && res.data) {
                setExpenses(res.data);
            }
        } catch {
            message.error(t('dispozitii.expenseFetchError'));
        }
    }, [selectedDate, makeRequest, t]);

    useEffect(() => {
        fetchExpenses();
    }, [fetchExpenses]);

    const fetchTransferTargets = useCallback(async () => {
        setTransferTargetsLoading(true);
        try {
            const query = cashPointId ? `?cashPointId=${cashPointId}` : '';
            const res = await makeRequest<TransferTargets>(`/dispozitii/transfer-targets${query}`);
            if (res.success && res.data) {
                setTransferTargets(res.data);
            }
        } catch {
            message.error(t('dispozitii.transferFetchError'));
        } finally {
            setTransferTargetsLoading(false);
        }
    }, [makeRequest, t, cashPointId]);

    useEffect(() => {
        fetchTransferTargets();
    }, [fetchTransferTargets]);

    const submitOperation = async (
        endpoint: string,
        successMessage: string,
        reset: () => void,
        body: Record<string, unknown>,
    ) => {
        setSaving(true);
        try {
            const res = await makeRequest<{ success: boolean }>(endpoint, {
                method: 'POST',
                body: JSON.stringify({ ...body, cashPointId: cashPointId ?? undefined }),
            });
            if (res.success) {
                message.success(successMessage);
                reset();
                await fetchSummary();
            } else {
                message.error(res.error ?? t('dispozitii.operationError'));
            }
        } catch {
            message.error(t('dispozitii.operationError'));
        } finally {
            setSaving(false);
        }
    };

    const submitAmountOperation = async (
        endpoint: string,
        state: AmountOperationState,
        successMessage: string,
        reset: () => void,
    ) => {
        if (!state.amount || state.amount <= 0) {
            message.warning(t('dispozitii.amountRequired'));
            return;
        }
        if (!state.reprezentand.trim()) {
            message.warning(t('dispozitii.reprezentandRequired'));
            return;
        }

        await submitOperation(endpoint, successMessage, reset, {
            amount: state.amount,
            reprezentand: state.reprezentand.trim(),
            delegat: state.delegat.trim() || undefined,
            act: state.act.trim() || undefined,
            rc: state.rc,
        });
    };

    const submitDenominationOperation = async (
        endpoint: string,
        state: DenominationOperationState,
        total: number,
        successMessage: string,
        reset: () => void,
    ) => {
        if (total <= 0) {
            message.warning(t('dispozitii.amountRequired'));
            return;
        }
        if (!state.reprezentand.trim()) {
            message.warning(t('dispozitii.reprezentandRequired'));
            return;
        }

        await submitOperation(endpoint, successMessage, reset, {
            denominations: state.denominations,
            reprezentand: state.reprezentand.trim(),
            delegat: state.delegat.trim() || undefined,
            act: state.act.trim() || undefined,
            rc: state.rc,
        });
    };

    const handleAssociateCollection = () =>
        submitAmountOperation(
            '/dispozitii/associate-collection',
            associateCollection,
            t('dispozitii.associateCollectionSuccess'),
            () => setAssociateCollection(emptyAmountState()),
        );

    const handleAssociatePayment = () =>
        submitAmountOperation(
            '/dispozitii/associate-payment',
            associatePayment,
            t('dispozitii.associatePaymentSuccess'),
            () => setAssociatePayment(emptyAmountState()),
        );

    const handleBankCollection = () =>
        submitAmountOperation(
            '/dispozitii/bank-collection',
            bankCollection,
            t('dispozitii.bankCollectionSuccess'),
            () => setBankCollection(emptyAmountState()),
        );

    const handleBankPayment = () =>
        submitAmountOperation(
            '/dispozitii/bank-payment',
            bankPayment,
            t('dispozitii.bankPaymentSuccess'),
            () => setBankPayment(emptyAmountState()),
        );

    const handleSupplierPayment = () =>
        submitAmountOperation(
            '/dispozitii/supplier-payment',
            supplierPayment,
            t('dispozitii.supplierPaymentSuccess'),
            () => setSupplierPayment(emptyAmountState()),
        );

    const handleSafePickup = () =>
        submitDenominationOperation(
            '/dispozitii/safe-pickup',
            safePickup,
            safePickupTotal,
            t('dispozitii.safePickupSuccess'),
            () => setSafePickup(emptyDenominationState()),
        );

    const handleTransfer = async () => {
        if (transferTotal <= 0) {
            message.warning(t('dispozitii.amountRequired'));
            return;
        }
        if (!transferToCashPointId) {
            message.warning(
                t('dispozitii.destinationCashPoint') + ' ' + t('dispozitii.amountRequired'),
            );
            return;
        }
        if (!transferReceiverUserId) {
            message.warning(t('dispozitii.receiverUser') + ' ' + t('dispozitii.amountRequired'));
            return;
        }
        setSaving(true);
        try {
            const res = await makeRequest<{ success: boolean }>('/dispozitii/transfer', {
                method: 'POST',
                body: JSON.stringify({
                    denominations: transferDenominations,
                    toCashPointId: transferToCashPointId,
                    receiverUserId: transferReceiverUserId,
                    observations: transferObservations.trim() || undefined,
                    cashPointId: cashPointId ?? undefined,
                }),
            });
            if (res.success) {
                message.success(t('dispozitii.transferSuccess'));
                setTransferDenominations(DEFAULT_DENOMINATIONS);
                setTransferToCashPointId(null);
                setTransferReceiverUserId(null);
                setTransferObservations('');
                await fetchSummary();
            } else {
                message.error(res.error ?? t('dispozitii.operationError'));
            }
        } catch {
            message.error(t('dispozitii.operationError'));
        } finally {
            setSaving(false);
        }
    };

    const handleAddExpense = async () => {
        if (expenseAmount === null || !Number.isFinite(expenseAmount) || expenseAmount <= 0) {
            message.warning(t('dispozitii.expenseAmountPositive'));
            return;
        }

        setExpenseSaving(true);
        try {
            const res = await makeRequest<{ success: boolean; id: number | null }>(
                '/dispozitii/expenses',
                {
                    method: 'POST',
                    body: JSON.stringify({
                        recordDate: selectedDate.format('YYYY-MM-DD'),
                        expenseType,
                        description: expenseDescription.trim() || undefined,
                        amount: expenseAmount,
                        notes: expenseNotes.trim() || undefined,
                        cashPointId: cashPointId ?? undefined,
                    }),
                },
            );

            if (res.success) {
                message.success(t('dispozitii.expenseAddSuccess'));
                setExpenseDescription('');
                setExpenseAmount(null);
                setExpenseNotes('');
                await fetchExpenses();
            } else {
                message.error(res.error ?? t('dispozitii.expenseAddError'));
            }
        } catch {
            message.error(t('dispozitii.expenseAddError'));
        } finally {
            setExpenseSaving(false);
        }
    };

    const updateAmountState = (
        setter: React.Dispatch<React.SetStateAction<AmountOperationState>>,
        patch: Partial<AmountOperationState>,
    ) => {
        setter(prev => ({ ...prev, ...patch }));
    };

    const updateDenominationState = (
        setter: React.Dispatch<React.SetStateAction<DenominationOperationState>>,
        key: keyof Denominations,
        value: number | null,
    ) => {
        setter(prev => ({
            ...prev,
            denominations: { ...prev.denominations, [key]: value ?? 0 },
        }));
    };

    const amountFormFields = (
        state: AmountOperationState,
        onChange: (patch: Partial<AmountOperationState>) => void,
    ) => (
        <>
            <Form.Item label={t('dispozitii.amount')} required>
                <InputNumber
                    min={0}
                    precision={2}
                    value={state.amount}
                    onChange={amount => onChange({ amount })}
                    style={{ width: 200 }}
                    placeholder="0.00"
                />
            </Form.Item>
            <Form.Item label={t('dispozitii.reprezentand')} required>
                <Input
                    value={state.reprezentand}
                    onChange={e => onChange({ reprezentand: e.target.value })}
                    maxLength={100}
                />
            </Form.Item>
            <Form.Item label={t('dispozitii.delegat')}>
                <Input
                    value={state.delegat}
                    onChange={e => onChange({ delegat: e.target.value })}
                    maxLength={50}
                />
            </Form.Item>
            <Form.Item label={t('dispozitii.act')}>
                <Input
                    value={state.act}
                    onChange={e => onChange({ act: e.target.value })}
                    maxLength={20}
                />
            </Form.Item>
            <Form.Item>
                <Checkbox checked={state.rc} onChange={e => onChange({ rc: e.target.checked })}>
                    {t('dispozitii.rc')}
                </Checkbox>
            </Form.Item>
        </>
    );

    const denominationFormFields = (
        state: DenominationOperationState,
        onDenominationChange: (key: keyof Denominations, value: number | null) => void,
        onTextChange: (patch: Partial<DenominationOperationState>) => void,
        total: number,
    ) => (
        <>
            <Form.Item label={t('dispozitii.denominations')}>
                <Row gutter={[8, 8]}>
                    {DENOMINATION_LABELS.map(item => (
                        <Col span={6} key={item.key}>
                            <Form.Item label={`${item.value} ${item.unit}`}>
                                <InputNumber
                                    min={0}
                                    value={state.denominations[item.key]}
                                    onChange={val => onDenominationChange(item.key, val)}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                    ))}
                </Row>
            </Form.Item>
            <Form.Item>
                <Statistic title={t('dispozitii.total')} value={total} precision={2} suffix="RON" />
            </Form.Item>
            <Form.Item label={t('dispozitii.reprezentand')} required>
                <Input
                    value={state.reprezentand}
                    onChange={e => onTextChange({ reprezentand: e.target.value })}
                    maxLength={100}
                />
            </Form.Item>
            <Form.Item label={t('dispozitii.delegat')}>
                <Input
                    value={state.delegat}
                    onChange={e => onTextChange({ delegat: e.target.value })}
                    maxLength={50}
                />
            </Form.Item>
            <Form.Item label={t('dispozitii.act')}>
                <Input
                    value={state.act}
                    onChange={e => onTextChange({ act: e.target.value })}
                    maxLength={20}
                />
            </Form.Item>
            <Form.Item>
                <Checkbox checked={state.rc} onChange={e => onTextChange({ rc: e.target.checked })}>
                    {t('dispozitii.rc')}
                </Checkbox>
            </Form.Item>
        </>
    );

    const dispositionColumns = [
        { title: t('dispozitii.colNr'), dataIndex: 'nr', key: 'nr', width: 60 },
        { title: t('dispozitii.colTip'), dataIndex: 'tip', key: 'tip', width: 80 },
        {
            title: t('dispozitii.colSuma'),
            dataIndex: 'suma',
            key: 'suma',
            width: 120,
            render: (val: number) => val.toFixed(2),
        },
        {
            title: t('dispozitii.colReprezentand'),
            dataIndex: 'reprezentand',
            key: 'reprezentand',
        },
        { title: t('dispozitii.colUser'), dataIndex: 'user', key: 'user', width: 100 },
        {
            title: t('dispozitii.colRc'),
            dataIndex: 'rc',
            key: 'rc',
            width: 60,
            render: (val: number) => (val ? '✓' : ''),
        },
    ];

    const expenseColumns = [
        {
            title: t('dispozitii.expenseType'),
            dataIndex: 'expenseType',
            key: 'expenseType',
            width: 150,
            render: (value: ExpenseType) =>
                t(`dispozitii.expenseTypes.${value}`, value.replace('_', ' ')),
        },
        {
            title: t('dispozitii.description'),
            dataIndex: 'description',
            key: 'description',
            render: (value: string | null) => value ?? '-',
        },
        {
            title: t('dispozitii.amount'),
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            render: (value: number) =>
                new Intl.NumberFormat('ro-RO', {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                }).format(value ?? 0),
        },
        {
            title: t('dispozitii.observations'),
            dataIndex: 'notes',
            key: 'notes',
            render: (value: string | null) => value ?? '-',
        },
    ];

    const tabItems = [
        {
            key: 'associates',
            label: (
                <span>
                    <TeamOutlined /> {t('dispozitii.associatesTab')}
                </span>
            ),
            children: (
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Card title={t('dispozitii.associateCollection')}>
                            <Form layout="vertical">
                                {amountFormFields(associateCollection, patch =>
                                    updateAmountState(setAssociateCollection, patch),
                                )}
                                <Button
                                    type="primary"
                                    icon={<PlusCircleOutlined />}
                                    onClick={handleAssociateCollection}
                                    loading={saving}
                                    disabled={bookingBlocked}
                                >
                                    {t('dispozitii.save')}
                                </Button>
                            </Form>
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card title={t('dispozitii.associatePayment')}>
                            <Form layout="vertical">
                                {amountFormFields(associatePayment, patch =>
                                    updateAmountState(setAssociatePayment, patch),
                                )}
                                <Button
                                    type="primary"
                                    icon={<MinusCircleOutlined />}
                                    onClick={handleAssociatePayment}
                                    loading={saving}
                                    disabled={bookingBlocked}
                                >
                                    {t('dispozitii.save')}
                                </Button>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            ),
        },
        {
            key: 'bank',
            label: (
                <span>
                    <BankOutlined /> {t('dispozitii.bankTab')}
                </span>
            ),
            children: (
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Card title={t('dispozitii.bankCollection')}>
                            <Form layout="vertical">
                                {amountFormFields(bankCollection, patch =>
                                    updateAmountState(setBankCollection, patch),
                                )}
                                <Button
                                    type="primary"
                                    icon={<PlusCircleOutlined />}
                                    onClick={handleBankCollection}
                                    loading={saving}
                                    disabled={bookingBlocked}
                                >
                                    {t('dispozitii.save')}
                                </Button>
                            </Form>
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card title={t('dispozitii.bankPayment')}>
                            <Form layout="vertical">
                                {amountFormFields(bankPayment, patch =>
                                    updateAmountState(setBankPayment, patch),
                                )}
                                <Button
                                    type="primary"
                                    icon={<MinusCircleOutlined />}
                                    onClick={handleBankPayment}
                                    loading={saving}
                                    disabled={bookingBlocked}
                                >
                                    {t('dispozitii.save')}
                                </Button>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            ),
        },
        {
            key: 'suppliers',
            label: (
                <span>
                    <ShopOutlined /> {t('dispozitii.suppliersTab')}
                </span>
            ),
            children: (
                <Card title={t('dispozitii.supplierPayment')}>
                    <Form layout="vertical">
                        {amountFormFields(supplierPayment, patch =>
                            updateAmountState(setSupplierPayment, patch),
                        )}
                        <Button
                            type="primary"
                            icon={<MinusCircleOutlined />}
                            onClick={handleSupplierPayment}
                            loading={saving}
                            disabled={bookingBlocked}
                        >
                            {t('dispozitii.save')}
                        </Button>
                    </Form>
                </Card>
            ),
        },
        {
            key: 'expenses',
            label: (
                <span>
                    <DollarOutlined /> {t('dispozitii.expensesTab')}
                </span>
            ),
            children: (
                <Card title={t('dispozitii.expensesTab')}>
                    {!expenseDayIsToday && (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 8 }}
                            message={t('dispozitii.expenseTodayOnly')}
                            data-testid="expense-today-only"
                        />
                    )}
                    <Row gutter={[8, 8]} align="middle">
                        <Col xs={24} md={6}>
                            <Select
                                value={expenseType}
                                onChange={(value: ExpenseType) => setExpenseType(value)}
                                style={{ width: '100%' }}
                                options={EXPENSE_TYPES.map(type => ({
                                    value: type,
                                    label: t(
                                        `dispozitii.expenseTypes.${type}`,
                                        type.replace('_', ' '),
                                    ),
                                }))}
                            />
                        </Col>
                        <Col xs={24} md={6}>
                            <Input
                                value={expenseDescription}
                                onChange={event => setExpenseDescription(event.target.value)}
                                placeholder={t('dispozitii.description')}
                            />
                        </Col>
                        <Col xs={24} md={4}>
                            <InputNumber
                                value={expenseAmount}
                                onChange={value => setExpenseAmount(value)}
                                min={0}
                                precision={2}
                                placeholder={t('dispozitii.amount')}
                                style={{ width: '100%' }}
                            />
                        </Col>
                        <Col xs={24} md={6}>
                            <Input
                                value={expenseNotes}
                                onChange={event => setExpenseNotes(event.target.value)}
                                placeholder={t('dispozitii.observations')}
                            />
                        </Col>
                        <Col xs={24} md={2}>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={expenseSaving}
                                disabled={!expenseDayIsToday || bookingBlocked}
                                onClick={handleAddExpense}
                                title={t('dispozitii.addExpense')}
                                style={{ width: '100%' }}
                            />
                        </Col>
                    </Row>
                    <Table
                        dataSource={expenses}
                        columns={expenseColumns}
                        size="small"
                        pagination={false}
                        rowKey="id"
                        style={{ marginTop: 12 }}
                    />
                </Card>
            ),
        },
        {
            key: 'transfers',
            label: (
                <span>
                    <SwapOutlined /> {t('dispozitii.transfersTab')}
                </span>
            ),
            children: (
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Card title={t('dispozitii.transfer')}>
                            <Form layout="vertical">
                                <Form.Item label={t('dispozitii.denominations')}>
                                    <Row gutter={[8, 8]}>
                                        {DENOMINATION_LABELS.map(item => (
                                            <Col span={6} key={item.key}>
                                                <Form.Item label={`${item.value} ${item.unit}`}>
                                                    <InputNumber
                                                        min={0}
                                                        value={transferDenominations[item.key]}
                                                        onChange={val =>
                                                            setTransferDenominations(prev => ({
                                                                ...prev,
                                                                [item.key]: val ?? 0,
                                                            }))
                                                        }
                                                        style={{ width: '100%' }}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        ))}
                                    </Row>
                                </Form.Item>
                                <Form.Item>
                                    <Statistic
                                        title={t('dispozitii.total')}
                                        value={transferTotal}
                                        precision={2}
                                        suffix="RON"
                                    />
                                </Form.Item>
                                <Form.Item label={t('dispozitii.destinationCashPoint')} required>
                                    <Select
                                        placeholder={t('dispozitii.selectCashPoint')}
                                        value={transferToCashPointId}
                                        onChange={setTransferToCashPointId}
                                        loading={transferTargetsLoading}
                                        style={{ width: 300 }}
                                    >
                                        {transferTargets.cashPoints.map(cp => (
                                            <Select.Option key={cp.id} value={cp.id}>
                                                {cp.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item label={t('dispozitii.receiverUser')} required>
                                    <Select
                                        placeholder={t('dispozitii.selectUser')}
                                        value={transferReceiverUserId}
                                        onChange={setTransferReceiverUserId}
                                        loading={transferTargetsLoading}
                                        style={{ width: 300 }}
                                    >
                                        {transferTargets.users.map(u => (
                                            <Select.Option key={u.id} value={u.id}>
                                                {u.username}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item label={t('dispozitii.observations')}>
                                    <Input.TextArea
                                        value={transferObservations}
                                        onChange={e => setTransferObservations(e.target.value)}
                                        maxLength={255}
                                        rows={3}
                                        style={{ width: 400 }}
                                    />
                                </Form.Item>
                                <Button
                                    type="primary"
                                    icon={<SwapOutlined />}
                                    onClick={handleTransfer}
                                    loading={saving}
                                    disabled={bookingBlocked}
                                >
                                    {t('dispozitii.save')}
                                </Button>
                            </Form>
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card title={t('dispozitii.safePickup')}>
                            <Form layout="vertical">
                                {denominationFormFields(
                                    safePickup,
                                    (key, value) =>
                                        updateDenominationState(setSafePickup, key, value),
                                    patch => setSafePickup(prev => ({ ...prev, ...patch })),
                                    safePickupTotal,
                                )}
                                <Button
                                    type="primary"
                                    icon={<SafetyCertificateOutlined />}
                                    onClick={handleSafePickup}
                                    loading={saving}
                                    disabled={bookingBlocked}
                                >
                                    {t('dispozitii.save')}
                                </Button>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div
                className="page-header"
                style={{ ['--page-header-accent' as string]: '#0369a1' }}
            >
                <div className="page-header__lead">
                    <span className="page-header__icon">
                        <DollarOutlined aria-hidden />
                    </span>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            {t('dispozitii.title')}
                        </Title>
                        <Text type="secondary">{t('dispozitii.subtitle')}</Text>
                    </div>
                </div>
            </div>

            <CashBookingRefusalNotice refusalKey={bookingRefusalKey} />

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title={t('dispozitii.safeBalance')}
                            value={summary.safeBalance}
                            precision={2}
                            prefix={<SafetyCertificateOutlined />}
                            suffix="RON"
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title={t('dispozitii.bankBalance')}
                            value={summary.bankBalance}
                            precision={2}
                            prefix={<BankOutlined />}
                            suffix="RON"
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Form.Item label={t('dispozitii.date')} style={{ marginBottom: 0 }}>
                            <DatePicker
                                value={selectedDate}
                                onChange={d => d && setSelectedDate(d)}
                            />
                        </Form.Item>
                    </Card>
                </Col>
            </Row>

            <Card style={{ marginBottom: 16 }}>
                <Tabs items={tabItems} />
            </Card>

            <Card title={t('dispozitii.todaysDispositions')}>
                <Table<DispozitiiRow>
                    loading={loading}
                    dataSource={summary.dispositions}
                    columns={dispositionColumns}
                    rowKey="id"
                    size="small"
                    pagination={{ pageSize: 20 }}
                />
            </Card>
        </div>
    );
};

export default Dispozitii;
